/**
 * Twinkle Cart — drawer shell
 * @module cart-drawer
 * Depends on: cart-items.js, global.js (trapFocus)
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, { openDrawer = true } = {}) {
    /* The empty-cart flag lives on THIS element — `<cart-drawer class="drawer
       is-empty">` in cart-drawer.liquid — and every rule that acts on it is
       written `cart-drawer.is-empty ...`. Clearing it from `.drawer__inner`,
       which never carries it, was a no-op.

       So adding the first item to an empty cart left the flag standing: the
       row was rendered into the DOM and then hidden by CSS, leaving a blank
       drawer above a footer reading "SUBTOTAL (1 ITEM)". Reported 2026-09-01
       with a screenshot of exactly that, and reproduced on the dev store by
       emptying the bag and adding one backdrop.

       Set AFTER the sections are injected, and derived from what actually
       rendered rather than from the payload: `TwinkleCart.renderDrawerResponse`
       has an `item_count` to read, but the studio's two call sites pass a
       sections-only payload and a `/cart/add.js` response, and neither carries
       one. Counting the rows that landed is the one signal every caller has.

       Toggled rather than removed, because this also runs on removal: a drawer
       that has just emptied needs the flag put BACK, or it shows an empty
       table where "your bag is empty" belongs. */
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      const innerHTML = TwinkleCart.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHTML) sectionElement.innerHTML = innerHTML;
    });

    const isEmpty = this.querySelectorAll('#CartDrawer-CartItems tr.cart-item').length === 0;
    this.classList.toggle('is-empty', isEmpty);
    // Same flag, second consumer: `cart-drawer-items.is-empty + .drawer__footer`
    // is what hides the subtotal, which is the half that stayed visible and
    // made the blank drawer look like a bug in the cart rather than in the CSS.
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
    this.querySelector('.drawer__footer')?.classList.toggle('is-empty', isEmpty);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (openDrawer) {
        this.open();
      } else if (this.classList.contains('active')) {
        this.close();
      }
    });

    document.querySelector('localization-form')?.changeCity();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return TwinkleCart.getSectionInnerHTML(html, selector);
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
